import React from 'react';
import { Link } from 'react-router-dom';
import QuoteWidget from '@/components/QuoteWidget';
import { Shield, CheckCircle2, Settings, TrendingUp, Layers } from 'lucide-react';

const UniversalLifePage = () => {
  const advantages = [
    { icon: Settings, title: "Flexible premium structure", description: "Adjust payments based on your cash flow" },
    { icon: Layers, title: "Adjustable death benefit", description: "Increase or decrease coverage as needs change" },
    { icon: TrendingUp, title: "Cash value growth potential", description: "Earn interest based on market performance" },
    { icon: Shield, title: "Lifetime coverage", description: "Protection that can last your entire life" }
  ];

  const types = [
    { name: "Fixed Universal Life", desc: "Earns a declared interest rate set by the carrier" },
    { name: "Indexed Universal Life (IUL)", desc: "Cash value tied to market index performance with downside protection" },
    { name: "Variable Universal Life (VUL)", desc: "Invest cash value in sub-accounts for growth potential" }
  ];

  return (
    <div className="bg-[#020617] min-h-screen pt-20" data-testid="universal-life-page">
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div>
              <Link to="/services" className="text-[#C5A059] text-sm uppercase tracking-widest mb-4 inline-block hover:text-[#D4AF37]">
                ← Back to Services
              </Link>
              <h1 className="font-serif text-4xl md:text-5xl text-white leading-tight mb-6">
                Universal Life Insurance
              </h1>
              <p className="text-xl text-slate-300 mb-6">
                Flexible Permanent Life Insurance
              </p>
              <p className="text-slate-400 mb-8 leading-relaxed">
                Universal life insurance combines permanent protection with flexible premiums and adjustable benefits. It's designed for those who want lifetime coverage with the ability to adapt to changing financial circumstances.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
                {advantages.map((item, idx) => {
                  const Icon = item.icon;
                  return (
                    <div key={idx} className="flex items-start space-x-3">
                      <div className="w-10 h-10 rounded-lg bg-[#C5A059]/10 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-[#C5A059]" />
                      </div>
                      <div>
                        <p className="text-white font-medium">{item.title}</p>
                        <p className="text-slate-500 text-sm">{item.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div>
              <QuoteWidget />
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-serif text-3xl text-white mb-8 text-center">Types of Universal Life</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {types.map((type, idx) => (
              <div key={idx} className="bg-slate-900/50 border border-slate-800 p-6 hover:border-[#C5A059]/30 transition-colors">
                <h3 className="text-white font-semibold text-lg mb-3">{type.name}</h3>
                <p className="text-slate-400 text-sm">{type.desc}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-slate-500 mt-8 text-sm">
            Universal life policies can be structured to support long-term financial planning goals including retirement income supplementation and estate planning.
          </p>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-serif text-3xl text-white mb-6">Find the Right Universal Life Policy</h2>
          <p className="text-slate-400 mb-8">Our advisors can help you choose the best structure for your goals.</p>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center bg-[#C5A059] text-[#020617] hover:bg-[#D4AF37] font-semibold px-8 py-4 uppercase tracking-wider text-sm transition-all"
          >
            Schedule Consultation
          </Link>
        </div>
      </section>
    </div>
  );
};

export default UniversalLifePage;
